import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Target, Users, Shield, Zap, Award, Heart } from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: Target,
      title: 'Accuracy First',
      description: 'All our calculators use verified formulas and algorithms, reviewed by financial experts.',
    },
    {
      icon: Shield,
      title: 'Privacy Protection',
      description: 'Your data stays on your device. We never collect or store your financial information.',
    },
    {
      icon: Users,
      title: 'User-Centric Design',
      description: 'Simple, intuitive interfaces that anyone can use, regardless of financial expertise.',
    },
    {
      icon: Zap,
      title: 'Lightning Fast',
      description: 'Instant calculations with no waiting. Get results in real-time as you type.',
    },
    {
      icon: Award,
      title: 'Always Free',
      description: 'Complete access to all tools without hidden fees, subscriptions, or premium tiers.',
    },
    {
      icon: Heart,
      title: 'Community Driven',
      description: 'Built based on user feedback and continuously improved to meet your needs.',
    },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Hero Section */}
      <section className="gradient-hero py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-4" variant="outline">
              About Us
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-foreground mb-6">
              Empowering Financial Decisions with{' '}
              <span className="text-gradient-primary">Smart Tools</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              wealthcalc was born from a simple mission: make financial planning accessible to everyone. We believe
              that good financial decisions shouldn't require expensive software or complex spreadsheets.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4" variant="outline">
                Our Story
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground mb-6">
                Making Finance Simple for Everyone
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Founded in 2024, wealthcalc started with a vision to democratize financial planning. Our team of
                  developers, designers, and financial experts came together to create tools that remove the complexity
                  from financial calculations.
                </p>
                <p>
                  What began as a single EMI calculator has grown into a comprehensive suite of 27+ financial tools,
                  serving over 50,000 users worldwide. Every tool we create is built with the same core principles:
                  accuracy, simplicity, and privacy.
                </p>
                <p>
                  Today, we're proud to help individuals, families, and small businesses make informed financial
                  decisions every day. And we're just getting started.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-primary opacity-20 blur-3xl rounded-full"></div>
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop"
                alt="Team collaboration"
                className="relative rounded-2xl shadow-elegant"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4" variant="outline">
              Our Values
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground mb-4">
              What We Stand For
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              These principles guide every decision we make and every feature we build
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="hover-lift transition-smooth">
                  <CardHeader>
                    <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 text-primary mb-4">
                      <Icon className="w-7 h-7" />
                    </div>
                    <CardTitle className="text-xl">{value.title}</CardTitle>
                    <CardDescription>{value.description}</CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '27+', label: 'Financial Tools' },
              { value: '50K+', label: 'Active Users' },
              { value: '100K+', label: 'Calculations Made' },
              { value: '99.9%', label: 'Accuracy Rate' },
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-gradient-primary mb-2">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/5">
        <div className="container mx-auto px-4">
          <Card className="border-2 border-primary/20 shadow-primary max-w-4xl mx-auto">
            <CardContent className="p-8 md:p-12 text-center">
              <Badge className="mb-4" variant="outline">
                Our Mission
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground mb-6">
                To empower everyone with the tools they need to take control of their financial future
              </h2>
              <p className="text-lg text-muted-foreground">
                We believe financial literacy shouldn't be a luxury. By providing free, accurate, and easy-to-use
                financial tools, we're helping people make better decisions about their money, one calculation at a
                time.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
